// Club kit (ADR 0017): the kit is a set of ordinary cosmetic creations, one per slot, worn by the members like any
// created jersey. The quick kit turns two colours and a pattern into such a jersey design, with the colours baked in,
// so the kit looks the same on every member whatever their own team colour.
import { COSMETIC_SLOTS, type ClubJersey, type ClubKit, type ClubKitPiece, type CosmeticContent, type CosmeticSlot, type JerseyDesign, type JerseyLayer, type Worn } from '@gruvetto/contracts';

/** Darkens (f < 1) or lightens (f > 1) a #rrggbb colour. */
export function shadeHex(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255].map(v => Math.round(f < 1 ? v * f : v + (255 - v) * (f - 1)));
  return '#' + c.map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
}
const luminance = (hex: string) => { const n = parseInt(hex.slice(1), 16), c = [n >> 16, (n >> 8) & 255, n & 255].map(v => v / 255); return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]; };
/** Ink that reads on a colour: near-black on light, near-white on dark. */
export const inkOn = (hex: string) => (luminance(hex) > 0.5 ? '#141518' : '#f4f4f4');

const PATTERNS: Record<ClubJersey['pattern'], (color: string, accent: string) => JerseyLayer[]> = {
  plain: (color, accent) => [
    { type: 'band', color: accent, dir: 'v', pos: 0.09, size: 0.18 }, { type: 'band', color: accent, dir: 'v', pos: 0.91, size: 0.18 },
    { type: 'band', color: shadeHex(color, 0.55), dir: 'h', pos: 0.505, size: 0.03 },
  ],
  gradient: (color, accent) => [{ type: 'gradient', color: accent, angle: 0 }],
  stripes: (color, accent) => [{ type: 'stripes', color: accent, dir: 'h', count: 4, width: 0.5 }],
  dots: (color, accent) => [{ type: 'dots', color: accent, size: 0.25, cell: 32, stagger: true }],
  checker: (color, accent) => [{ type: 'checker', color: accent, cell: 32 }],
  chevrons: (color, accent) => [{ type: 'chevrons', color: accent, count: 3, width: 0.18 }],
  band: (color, accent) => [{ type: 'band', color: accent, dir: 'h', pos: 0.44, size: 0.16 }, { type: 'band', color: shadeHex(accent, 0.6), dir: 'h', pos: 0.535, size: 0.02 }],
};

/** The jersey design of a quick kit. Deterministic: the same kit always gives the same design. */
export function clubJerseyDesign(jersey: ClubJersey, tag: string): JerseyDesign {
  const color = jersey.color.toLowerCase(), accent = jersey.accent.toLowerCase();
  const layers = PATTERNS[jersey.pattern](color, accent);
  if (jersey.tag && tag) {
    // the chest band of the plain and band kits is the accent colour: the tag must read on it
    const onAccent = jersey.pattern === 'band';
    layers.push({ type: 'text', text: tag.slice(0, 4).toUpperCase(), color: inkOn(onAccent ? accent : color), size: 0.16, x: 0.5, y: onAccent ? 0.47 : 0.7, font: 'display', bold: true });
  }
  return { base: color, layers };
}
/** The jersey creation a quick kit becomes (content only; the server adds the manifest and the author). */
export const quickKitContent = (jersey: ClubJersey, tag: string): CosmeticContent => ({ slot: 'jersey', design: clubJerseyDesign(jersey, tag) });

/** The quick kit a new club starts from: blue, white side bands. */
export const DEFAULT_CLUB_KIT: ClubJersey = { pattern: 'plain', color: '#1f6fe0', accent: '#f4f4f4', tag: true };

/** Id of a worn club piece in a player's outfit (`look.wear[slot].id`); the jersey keeps its historical `club:<id>`. */
export const clubWornId = (clubId: string, slot: CosmeticSlot = 'jersey') => (slot === 'jersey' ? `club:${clubId}` : `club:${clubId}:${slot}`);
export const isClubWornId = (id: string | undefined | null) => !!id && id.startsWith('club:');
/** Whether a worn id belongs to this club's kit (any slot). */
export const isWornOfClub = (id: string | undefined | null, clubId: string) => !!id && (id === `club:${clubId}` || id.startsWith(`club:${clubId}:`));
/** The worn item a member puts on for a slot: embedded in the outfit, so other riders see it without knowing the club. */
export function clubWorn(clubId: string, slot: CosmeticSlot, piece: ClubKitPiece): Worn {
  return { id: clubWornId(clubId, slot), name: piece.name.slice(0, 60), content: piece.content };
}
/** Every piece of the kit as worn items, by slot. */
export function clubWear(clubId: string, kit: ClubKit): Partial<Record<CosmeticSlot, Worn>> {
  const out: Partial<Record<CosmeticSlot, Worn>> = {};
  for (const slot of COSMETIC_SLOTS) { const p = kit[slot]; if (p && p.content.slot === slot) out[slot] = clubWorn(clubId, slot, p); }
  return out;
}

const HEX = /^#[0-9a-f]{6}$/i;
/**
 * Team colour of a kit: the jersey's base colour when it is a fixed colour, otherwise its first fixed layer colour,
 * otherwise null (a jersey painted in the team colour takes the wearer's own). Lets a member adopt the club colour.
 */
export function kitColor(kit: ClubKit): string | null {
  const j = kit.jersey?.content;
  if (!j || j.slot !== 'jersey') return null;
  if (HEX.test(j.design.base)) return j.design.base.toLowerCase();
  for (const l of j.design.layers) { const c = (l as { color?: string }).color; if (c && HEX.test(c)) return c.toLowerCase(); }
  return null;
}
